import { cn } from '@/lib/utils';

/*
 * The two chart shapes the Analytics screen needs, and no others.
 *
 * **Plain HTML with Tailwind, no charting library.** Two shapes, both of them
 * bars, is not a reason to add a dependency, a bundle and an API to learn — and
 * a `div` with a height is a chart somebody can read the source of. The moment
 * this screen wants a line, a stacked area or an axis with real tick maths, that
 * trade flips; it has not.
 *
 * ---
 *
 * **The rules every chart in here follows.** They are not stylistic:
 *
 *   - **A legend whenever there are two series**, and a coloured swatch beside a
 *     label. Colour alone never carries identity — that is the same rule the
 *     status badges follow, for the same colourblind reader.
 *   - **The numbers are always reachable as text.** Each bar carries its own
 *     figure in its accessible name, and the caller adds a table for the reader
 *     who wants all of them at once. A chart nobody can read the values off is
 *     a picture.
 *   - **Bars are focusable.** A tooltip that only appears on hover is a tooltip
 *     a keyboard user never sees, and the same `group-focus-within` that shows
 *     it for the mouse shows it for Tab.
 *   - **Zero draws a hairline, not nothing.** A month that billed nothing is a
 *     fact (P1); a gap in the row reads as missing data.
 */

// ---------------------------------------------------------------------------
// Vertical bars over time
// ---------------------------------------------------------------------------

export interface TimeBarPoint {
  /** The x-axis label — "August 2026", "2026-08-10". */
  label: string;
  /** Shown under the bar. Kept short: "Aug", "10". */
  tick: string;
  /** One value per series, in the same order as `series`. */
  values: number[];
}

export interface ChartSeries {
  name: string;
  /** A Tailwind background class from the `chart-*` tokens. */
  fill: string;
}

export function TimeBars({
  points,
  series,
  format,
  emptyLabel,
}: {
  points: TimeBarPoint[];
  series: ChartSeries[];
  format: (value: number) => string;
  /** Shown instead of the chart when every value is zero. */
  emptyLabel: string;
}) {
  /*
   * **One scale across every series** — the tallest single value anywhere sets
   * the height of everything. Two series on two scales is the commonest way a
   * chart lies, and here it would mean drawing a month that collected $200
   * level with a month that billed $8,000.
   */
  const peak = Math.max(...points.flatMap((point) => point.values), 0);

  if (peak === 0) {
    return <p className="py-8 text-center text-body italic text-ink-500">{emptyLabel}</p>;
  }

  return (
    <div>
      {series.length > 1 ? (
        <ul className="mb-4 flex flex-wrap gap-x-5 gap-y-2">
          {series.map((entry) => (
            <li key={entry.name} className="flex items-center gap-2">
              <span aria-hidden="true" className={cn('size-3 shrink-0 rounded-sm', entry.fill)} />
              {/* Text wears a text token. The swatch carries the identity. */}
              <span className="text-meta text-ink-700">{entry.name}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/*
        Horizontal scroll rather than squeezing thirty bars into 375px. The
        alternative is bars a pixel wide, which is not a smaller chart — it is
        no chart.

        **`pt-16` is what makes the tooltip visible, and it is not spacing.**
        CSS will not give one axis `auto` and leave the other `visible` — asking
        for horizontal scrolling silently clips vertically too, so the tooltip
        sitting above the tallest bar was cut in half by this very element. The
        padding is the headroom it opens into. Found by hovering a bar.
      */}
      <div className="-mx-1 overflow-x-auto px-1 pb-1 pt-20">
        <div
          className="flex min-w-full items-end gap-1"
          style={{ minWidth: `${String(points.length * (series.length > 1 ? 44 : 20))}px` }}
        >
          {points.map((point, column) => (
            <div key={point.label} className="group relative flex flex-1 flex-col items-center">
              <div className="flex h-40 w-full items-end justify-center gap-[2px]">
                {point.values.map((value, index) => (
                  <div
                    key={series[index]?.name ?? String(index)}
                    tabIndex={0}
                    // The whole figure, in words, for a screen reader and for
                    // the focus tooltip. `role="img"` so it is announced as one
                    // thing rather than as an empty element.
                    role="img"
                    aria-label={`${point.label}, ${series[index]?.name ?? ''}: ${format(value)}`}
                    className={cn(
                      // `4px` rounded top, anchored to the baseline — the bar
                      // grows from the axis and is never a floating capsule.
                      'w-full rounded-t-[4px] transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-honey-700 focus-visible:ring-offset-1',
                      'group-hover:opacity-80',
                      series[index]?.fill ?? 'bg-chart-1',
                    )}
                    style={{
                      // A real zero still draws 2px, so the reader can see the
                      // bar is there and empty rather than absent.
                      height: value === 0 ? '2px' : `${String(Math.max(2, (value / peak) * 100))}%`,
                    }}
                  />
                ))}
              </div>

              {/*
                The tooltip. Shown for the mouse and for the keyboard by the same
                rule, and `pointer-events-none` so it can never sit between the
                cursor and the bar underneath it.

                **The end columns anchor inward instead of centring**, because
                the scroll container clips sideways as well as vertically. A
                centred tooltip on the last bar — which is *this* month, the one
                anybody looks at first — lost its right-hand half off the edge.
              */}
              <div
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute bottom-full z-10 mb-1 hidden whitespace-nowrap rounded-sm bg-ink-900 px-2 py-1 text-meta text-white shadow-pop group-focus-within:block group-hover:block',
                  column === 0
                    ? 'left-0'
                    : column === points.length - 1
                      ? 'right-0'
                      : 'left-1/2 -translate-x-1/2',
                )}
              >
                <p className="font-medium">{point.label}</p>
                {point.values.map((value, index) => (
                  <p key={series[index]?.name ?? String(index)}>
                    {series.length > 1 ? `${series[index]?.name ?? ''}: ` : ''}
                    {format(value)}
                  </p>
                ))}
              </div>

              <span aria-hidden="true" className="mt-2 text-meta text-ink-500">
                {point.tick}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Horizontal ranked bars
// ---------------------------------------------------------------------------

export interface RankedBar {
  label: string;
  value: number;
  /**
   * A second, smaller figure drawn inside the first — conversions out of a
   * total.
   *
   * **Its own words, not the bar's formatter.** The outer figure says what it
   * counts ("31 enquiries"); running the inner one through the same formatter
   * produced "4 enquiries became a customer", which is not a sentence.
   */
  inner?: { value: number; label: string };
}

/**
 * A ranked list where the bar is the comparison and the number is the answer.
 *
 * **The figure is always printed beside the bar**, never left to be estimated
 * from its length. Length is what makes the ranking readable at a glance; the
 * number is what somebody acts on, and a chart that makes them measure it with
 * their eye has kept the decoration and lost the data.
 */
export function RankedBars({
  bars,
  format,
  emptyLabel,
}: {
  bars: RankedBar[];
  format: (value: number) => string;
  emptyLabel: string;
}) {
  const peak = Math.max(...bars.map((bar) => bar.value), 0);

  if (peak === 0) {
    return <p className="py-6 text-center text-body italic text-ink-500">{emptyLabel}</p>;
  }

  return (
    <ul className="space-y-3">
      {bars.map((bar) => (
        <li key={bar.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-body text-ink-900">{bar.label}</span>
            <span className="shrink-0 text-body tabular-nums text-ink-700">
              {format(bar.value)}
              {bar.inner ? (
                <span className="ml-2 text-meta text-ink-500">
                  · {bar.inner.value.toLocaleString()} {bar.inner.label}
                </span>
              ) : null}
            </span>
          </div>

          <div
            className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full bg-ink-100"
            role="img"
            aria-label={
              bar.inner
                ? `${bar.label}: ${format(bar.value)}, of which ${bar.inner.value.toLocaleString()} ${bar.inner.label}`
                : `${bar.label}: ${format(bar.value)}`
            }
          >
            <div
              className="h-full rounded-full bg-chart-1"
              style={{ width: `${String((bar.value / peak) * 100)}%` }}
            >
              {/*
                The conversions, drawn inside their own total so the two are
                read as a part and a whole rather than as two competing bars.
                A 2px ring in the surface colour keeps the edge legible where
                the fills meet.
              */}
              {bar.inner ? (
                <div
                  className="h-full rounded-full bg-chart-2 ring-2 ring-white"
                  style={{
                    width: `${String(bar.value === 0 ? 0 : (bar.inner.value / bar.value) * 100)}%`,
                  }}
                />
              ) : null}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
